import { useQueryClient } from '@tanstack/react-query';
import { trackOnFacebook } from 'app/lib/analytics';
import { useQueryKeys } from 'app/network/invalidation';
import { ListingsSortBy } from 'app/types/listing';
import type { User } from 'app/types/user';
import { isMobileNative } from 'app/utils/web-helpers';
import { useRef } from 'react';
import { AppEventsLogger } from 'react-native-fbsdk-next';

interface UseSearchParams {
  search: string;
  setSearch: (query: string) => void;
  sortValue: ListingsSortBy;
  setSortValue: (value: ListingsSortBy) => void;
}

// @TODO: Move this in useQueryKeys hook ? for selfUser
export function optimisticUserRecentSearchesUpdate(
  client: ReturnType<typeof useQueryClient>,
  qk: ReturnType<typeof useQueryKeys>,
  query: string,
) {
  client.setQueryData<User>(qk.selfUser.key, (old) => {
    if (!old) return old;
    return { ...old, recentSearches: [query, ...(old.recentSearches ?? []).filter((s) => s !== query)] };
  });
}

export function useSearch({ search, setSearch, sortValue, setSortValue }: UseSearchParams) {
  const qk = useQueryKeys();
  const client = useQueryClient();

  const sortBeforeSearchRef = useRef(sortValue);

  const doSearch = (query: string) => {
    query = query.trim();
    const previousQuery = search.trim();
    if (query && !previousQuery && sortValue !== ListingsSortBy.Relevance) {
      sortBeforeSearchRef.current = sortValue;
      setSortValue(ListingsSortBy.Relevance);
    } else if (!query && previousQuery && sortValue === ListingsSortBy.Relevance) {
      setSortValue(sortBeforeSearchRef.current);
    }
    setSearch(query);
    if (query.length > 0) {
      optimisticUserRecentSearchesUpdate(client, qk, query);
      if (isMobileNative) {
        trackOnFacebook(AppEventsLogger.AppEvents.Searched, {
          [AppEventsLogger.AppEventParams.SearchString]: query,
        });
      }
    }
  };

  return { doSearch };
}
